/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { useLazyGetMetaMapDataQuery } from "@api/metaMapApi";
import { useEffect, useRef, useState } from "react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { MetaDataJson } from "@interfaces/index";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { PERSON_CODES_LEGAL_REPRESENTATIVE } from "@helpers/constantes";

const ValidarIndentidadPfae = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const personal = useSelector((state: RootState) => state.register.personal_data);
  const application = useSelector((state: RootState) => state.register.application);

  const metamapButtonRef = useRef<HTMLDivElement>(null);
  const [getDataMetaMap] = useLazyGetMetaMapDataQuery();

  const [enableButton, setEnableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [metaDataJson, setMetaDataJson] = useState<MetaDataJson | null>(null);
  const [getPersons] = useLazyGetPersonsQuery();

  const getMetamapData = async (accout_id: string) => {
    setIsLoading(true);

    const metamap_res = await getDataMetaMap(accout_id).unwrap();
    const metamap_info = metamap_res?.data;

    if (metamap_info.attempts > 3) {
      return;
    }

    setMetaDataJson({
      client_details_id: metamap_info.client_details_id,
      loan_application_id: metamap_info.loan_application_id,
      person_id: metamap_info.person_id,
      person_type: metamap_info.person_type,
      regimen: metamap_info.regimen,
      attempts: metamap_info.attempts,
      upload_type: metamap_info.upload_type,
    });

    setEnableButton(true);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const persons = await getPersons({
          loanApplicationId: application.id,
        }).unwrap();

        const representantes = persons.filter((item) =>
          PERSON_CODES_LEGAL_REPRESENTATIVE.includes(item.person_type?.code || ""),
        );

        const representante = representantes[0];

        if (!representante.id) {
          goBack();
          return;
        }

        if (representante.identity_validation_completed) {
          goBack();
          return;
        }

        await getMetamapData(representante.id);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ValidacionIdentidadPersona-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleUserFinishedSdk = (_event: CustomEvent) => {
      setMetaDataJson(null);
      setEnableButton(false);
      goBack();
    };

    const metamapButton = metamapButtonRef.current;

    if (metamapButton) {
      metamapButton.addEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
    }

    return () => {
      if (metamapButton) {
        metamapButton.removeEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
      }
    };
  }, []);

  const goBack = () => {
    navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box my={1}>
            <Typography align={"center"} fontSize={"16px"} fontWeight={600} color={theme?.palette?.primary?.dark}>
              Validación identidad
            </Typography>
          </Box>

          <Typography
            fontSize={"14px"}
            fontWeight={400}
            color={theme?.palette?.primary?.main}
            align="center"
            mt={3}
            mb={3}
          >
            {`${personal.name} ${personal.first_lastname} ${personal.second_lastname}`}
          </Typography>

          <Typography
            align={"center"}
            fontSize={"14px"}
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
            mt={3}
            mb={3}
          >
            Validaremos tu identidad por medio de fotografías faciales y tu identificación oficial
          </Typography>

          <Box mt={4} sx={{ pointerEvents: isLoading ? "none" : "auto", opacity: isLoading ? 0.5 : 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pointerEvents: !enableButton ? "none" : "auto",
                opacity: !enableButton ? 0.5 : 1,
                cursor: !enableButton ? "not-allowed" : "pointer",
              }}
            >
              <metamap-button
                ref={metamapButtonRef}
                clientid={import.meta.env.VITE_META_CLIENT_ID}
                flowId={import.meta.env.VITE_META_FLOW_ID}
                merchantToken={import.meta.env.VITE_MERCHANT_TOKEN}
                language="es"
                metadata={JSON.stringify(metaDataJson || {})}
                color={theme?.palette?.primary?.main}
              />
            </Box>
          </Box>

          <Box display="flex" justifyContent="center" mt={4}>
            <Button variant="blue_outlined" type="button" onClick={() => goBack()}>
              Regresar al expediente
            </Button>
          </Box>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ValidarIndentidadPfae;
